/**
 * Sector operations — Member Phone Delivery Change Requests Section.
 * R39 E2.b (SO-P269, SO-P54, mockup m-del-crs).
 */

import React, { useEffect, useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Button } from '../../Common'
import { AppDialog } from '../../Common/AppDialog'
import { useNls } from '../../../nls/nlsProvider'
import { subscribeOpsEngagements } from '../../../services/opsDeliveryService'
import {
  subscribeChangeRequests,
  createChangeRequest,
} from '../../../services/opsDeliveryChangeRequestsService'
import {
  formatChangeRequestNumber,
  type OpsChangeRequest,
  type OpsEngagement,
} from '../../../types/ops'
import './delivery.css'

export interface MemberDeliveryChangeRequestsPhoneSectionProps {
  workspaceId: string
  isAdmin?: boolean
}

export const MemberDeliveryChangeRequestsPhoneSection: React.FC<
  MemberDeliveryChangeRequestsPhoneSectionProps
> = ({ workspaceId }) => {
  const { t } = useNls()

  const [engagements, setEngagements] = useState<OpsEngagement[]>([])
  const [changeRequests, setChangeRequests] = useState<OpsChangeRequest[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Form states
  const [formEngagementId, setFormEngagementId] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formWhatChanges, setFormWhatChanges] = useState('')
  const [formHours, setFormHours] = useState('8')
  const [formRelease, setFormRelease] = useState('')

  useEffect(() => {
    if (!workspaceId) return undefined
    const unsubEng = subscribeOpsEngagements(workspaceId, (list) => {
      setEngagements(list)
      if (list.length > 0 && !formEngagementId) {
        setFormEngagementId(list[0].id)
      }
    })
    const unsubCrs = subscribeChangeRequests(workspaceId, null, (list) => {
      setChangeRequests(list)
    })
    return () => {
      unsubEng()
      unsubCrs()
    }
  }, [workspaceId, formEngagementId])

  const engagementMap = useMemo(() => {
    const map = new Map<string, OpsEngagement>()
    engagements.forEach((e) => map.set(e.id, e))
    return map
  }, [engagements])

  const handleOpenCreate = () => {
    setFormEngagementId(engagements[0]?.id || '')
    setFormTitle('')
    setFormWhatChanges('')
    setFormHours('8')
    setFormRelease('')
    setErrorMessage(null)
    setDialogOpen(true)
  }

  const handleCreateCr = async () => {
    if (!formEngagementId) return
    setBusy(true)
    setErrorMessage(null)
    try {
      await createChangeRequest({
        workspaceId,
        engagementId: formEngagementId,
        title: formTitle.trim(),
        whatChanges: formWhatChanges.trim(),
        hoursEstimate: Number(formHours) || 1,
        releaseTag: formRelease.trim() || null,
      })
      setDialogOpen(false)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('NLSOpsDeliveryErrorGeneric')
      setErrorMessage(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="opsDeliveryPhoneSection"
      data-testid="member-delivery-change-requests-phone-section"
    >
      <div className="opsDeliveryHeader">
        <h3 className="opsDeliveryCardTitle">{t('NLSOpsDeliveryPhoneChangesTitle')}</h3>
      </div>

      <div className="opsDeliveryList" data-testid="phone-crs-list">
        {changeRequests.map((cr) => {
          const eng = engagementMap.get(cr.engagementId)
          const isDraft = cr.status === 'DRAFT'
          const isSent = cr.status === 'SENT'
          const isApproved = cr.status === 'APPROVED'
          const isRevised = cr.status === 'REVISION_REQUESTED'
          const isRejected = cr.status === 'REJECTED'

          return (
            <div
              key={cr.id}
              className="opsDeliveryPhoneCard"
              data-testid={`phone-cr-card-${cr.id}`}
            >
              <div className="opsDeliveryPhoneRow">
                <b className="opsDeliveryCardTitle">
                  {formatChangeRequestNumber(cr.number)} · {cr.title}
                </b>
                <span
                  className={
                    isApproved
                      ? 'opsDeliveryPill opsDeliveryPill--good'
                      : isSent
                        ? 'opsDeliveryPill opsDeliveryPill--warn'
                        : isRevised
                          ? 'opsDeliveryPill opsDeliveryPill--info'
                          : isDraft
                            ? 'opsDeliveryPill opsDeliveryPill--bad'
                            : isRejected
                              ? 'opsDeliveryPill opsDeliveryPill--bad'
                              : 'opsDeliveryPill opsDeliveryPill--neutral'
                  }
                >
                  {isSent
                    ? t('NLSOpsDeliveryPhoneWithClient')
                    : isDraft
                      ? t('NLSOpsDeliveryCrsStatusDraft')
                      : isApproved
                        ? t('NLSOpsDeliveryCrsStatusApproved')
                        : isRevised
                          ? t('NLSOpsDeliveryCrsStatusRevised')
                          : isRejected
                            ? t('NLSOpsDeliveryCrsStatusRejected')
                            : t('NLSOpsDeliveryCrsStatusWithdrawn')}
                </span>
              </div>

              <div className="opsDeliveryKpiDesc">
                <span>{cr.hoursEstimate} h</span>
                {eng && <span> · {eng.name}</span>}
                {cr.unbilledHours && cr.unbilledHours > 0 ? (
                  <span>
                    {' '}
                    · {t('NLSOpsDeliveryPhoneLogged', { hours: String(cr.unbilledHours) })}
                  </span>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>

      <Button
        variant="primary"
        onClick={handleOpenCreate}
        data-testid="phone-raise-cr-btn"
      >
        <AddRoundedIcon />
        <span>{t('NLSOpsDeliveryPhoneNewCrBtn')}</span>
      </Button>

      {/* Raise CR Dialog */}
      <AppDialog
        open={dialogOpen}
        title={t('NLSOpsDeliveryCrsDialogTitle')}
        onDismiss={() => setDialogOpen(false)}
        actions={[
          {
            label: t('NLSOpsDeliveryAllocationsEndAction'),
            variant: 'secondary',
            onClick: () => setDialogOpen(false),
          },
          {
            label: t('NLSOpsDeliveryCrsBtnNew'),
            variant: 'primary',
            disabled: busy,
            onClick: handleCreateCr,
          },
        ]}
      >
        <div className="opsDeliveryFormGrid">
          {errorMessage && <div className="opsDeliveryErrorBanner">{errorMessage}</div>}

          <div className="opsDeliveryFormField">
            <label className="opsDeliveryFormLabel">{t('NLSOpsDeliveryFieldClientSelect')}</label>
            <select
              className="opsDeliveryFilterSelect"
              value={formEngagementId}
              onChange={(e) => setFormEngagementId(e.target.value)}
              aria-label={t('NLSOpsDeliveryFieldClientSelect')}
            >
              {engagements.map((eng) => (
                <option key={eng.id} value={eng.id}>
                  {eng.name}
                </option>
              ))}
            </select>
          </div>

          <div className="opsDeliveryFormField">
            <label className="opsDeliveryFormLabel">{t('NLSOpsDeliveryCrsFieldTitle')}</label>
            <input
              type="text"
              className="inp"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder={t('NLSOpsDeliveryPlaceholderTitle')}
              aria-label={t('NLSOpsDeliveryCrsFieldTitle')}
            />
          </div>

          <div className="opsDeliveryFormField">
            <label className="opsDeliveryFormLabel">{t('NLSOpsDeliveryCrsFieldWhatChanges')}</label>
            <textarea
              className="inp"
              rows={3}
              value={formWhatChanges}
              onChange={(e) => setFormWhatChanges(e.target.value)}
              aria-label={t('NLSOpsDeliveryCrsFieldWhatChanges')}
            />
          </div>

          <div className="opsDeliveryFormField">
            <label className="opsDeliveryFormLabel">{t('NLSOpsDeliveryCrsFieldHours')}</label>
            <input
              type="number"
              step="0.5"
              min="0.5"
              max="10000"
              className="inp"
              value={formHours}
              onChange={(e) => setFormHours(e.target.value)}
              aria-label={t('NLSOpsDeliveryCrsFieldHours')}
            />
          </div>

          <div className="opsDeliveryFormField">
            <label className="opsDeliveryFormLabel">{t('NLSOpsDeliveryCrsFieldRelease')}</label>
            <input
              type="text"
              className="inp"
              value={formRelease}
              onChange={(e) => setFormRelease(e.target.value)}
              aria-label={t('NLSOpsDeliveryCrsFieldRelease')}
            />
          </div>
        </div>
      </AppDialog>
    </div>
  )
}
